// covered-by: packages/schemas/src/christo/__tests__/chatAtomComponentMap.test.ts
import chatAtomComponentMap from "./chatAtomComponentMap.json";

// why: `ask-puntt.chat-atom.*` ids live in @mg/schemas so rembrandt and carrie share one canonical list (see chatAtomComponentMap.json).
/**
 * Stable agent-referenceable IDs for Ask Puntt chat atoms (PR #5110).
 * Rembrandt / coordinator agents should emit these ids when selecting UI to render.
 */
export const CHAT_ATOM_AGENT_COMPONENT_IDS = {
  choiceRow: "ask-puntt.chat-atom.choice-row",
  questionFormCard: "ask-puntt.chat-atom.question-form-card",
  questionForm: "ask-puntt.chat-atom.question-form",
  questionFormAnswer: "ask-puntt.chat-atom.question-form-answer",
  reviewPreflight: "ask-puntt.chat-atom.review-preflight",
  enterpriseImpact: "ask-puntt.chat-atom.enterprise-impact",
  feedMemoryCard: "ask-puntt.chat-atom.feed-memory-card",
  reviewListCard: "ask-puntt.chat-atom.review-list-card",
  metricBlock: "ask-puntt.chat-atom.metric-block",
  metricBlockCondensed: "ask-puntt.chat-atom.metric-block-condensed",
  agentSteps: "ask-puntt.chat-atom.agent-steps",
  searchProgress: "ask-puntt.chat-atom.search-progress",
  thinkingState: "ask-puntt.chat-atom.thinking-state",
} as const;

export type ChatAtomAgentComponentId =
  (typeof CHAT_ATOM_AGENT_COMPONENT_IDS)[keyof typeof CHAT_ATOM_AGENT_COMPONENT_IDS];

export type ChatAtomAgentComponentDef = {
  id: ChatAtomAgentComponentId;
  /**
   * Carrie draws this atom from its own stream/UI state, so no
   * `render_component` payload ever carries it. Agent-REFERENCEABLE (it is in
   * the map and stamps `data-agent-component-id`) but not agent-RENDERABLE.
   */
  clientOnly?: boolean;
  /**
   * Renders only inside another atom's slot. warhol's `render_component`
   * refuses one on its own and Carrie's `isStandaloneChatAtom` drops one that
   * arrives anyway, so an agent that names it standalone draws NOTHING and is
   * told nothing. Orthogonal to `clientOnly`: a nested-only atom is still on
   * the wire, it just needs a parent.
   */
  nestedOnly?: boolean;
  component: string;
  sourceFile: string;
  description: string;
  whenToUse: string;
  keyProps: string[];
  variants?: string[];
};

// why: the stamped value is NOT always a bare `ChatAtomAgentComponentId`. An
// atom that draws more than once on a surface stamps the INSTANCE form
// `${baseId}.${instanceKey}`, so a consumer that compares the attribute with
// `===` against a base id silently misses every repeated tile. The shipped
// case is `metric-block-condensed`: a lone chip still stamps the base id, but
// EnterpriseImpact's grid stamps
// `ask-puntt.chat-atom.metric-block-condensed.<metricId>` (`.reviews`,
// `.hours`). Match a base id as
// `value === baseId || value.startsWith(`${baseId}.`)`.
/** DOM attribute agents and tests use to locate rendered chat atoms. */
export const AGENT_COMPONENT_ID_ATTR = "data-agent-component-id" as const;

export type ChatAtomAgentComponentInstanceId =
  `${ChatAtomAgentComponentId}.${string}`;

/**
 * Per-row instance id when the same atom renders more than once on a surface.
 * The base id stays the atom's identity — the suffix only disambiguates rows.
 */
export function chatAtomAgentInstanceId(
  baseId: ChatAtomAgentComponentId,
  instanceKey: string,
): ChatAtomAgentComponentInstanceId {
  return `${baseId}.${instanceKey}`;
}

export function agentComponentIdAttr(
  id: ChatAtomAgentComponentId | ChatAtomAgentComponentInstanceId,
): { [AGENT_COMPONENT_ID_ATTR]: string } {
  return { [AGENT_COMPONENT_ID_ATTR]: id };
}

/** Authoritative map for AI agents — `chatAtomComponentMap.json` in this directory. */
export const CHAT_ATOM_AGENT_COMPONENT_MAP =
  chatAtomComponentMap as ChatAtomAgentComponentDef[];

// why: the map is the whole agent-facing catalog, and it is WIDER than the wire
// vocabulary. `thinking-state`, `agent-steps` and `search-progress` are drawn
// from Carrie's own stream state, `metric-block-condensed` is what
// `MetricBlock size=compact` delegates to, and `question-form-answer` is an
// onboarding display -- an agent may name any of them, but none can arrive in a
// `render_component` payload, and `ChatAtoms.tsx` has no case for them. The
// christo tuple (`ASK_PUNTT_CHAT_ATOM_IDS`) and warhol's `ChatAtomId` are the
// wire vocabulary, so they must be compared against this subset, not the map.
// Both guards exist and both filter `clientOnly` before comparing:
// `__tests__/chatAtomComponentMap.test.ts` here pins the christo tuple to
// `CHAT_ATOM_AGENT_RENDERABLE_COMPONENT_IDS`, and
// `services/warhol/tests/contracts/test_chat_atom_parity.py` there drops
// `clientOnly` entries in its `_map_ids()` before the set-equality against
// `ChatAtomId` -- widening either one to the whole map is how the five
// client-only atoms get reported as drift (PR #5399) or, worse, accepted on
// the wire with nothing to draw.

/** Atoms Carrie draws itself; never sent over the christo/warhol wire. */
export const CHAT_ATOM_CLIENT_ONLY_COMPONENT_IDS: readonly ChatAtomAgentComponentId[] =
  CHAT_ATOM_AGENT_COMPONENT_MAP.filter((entry) => entry.clientOnly).map(
    (entry) => entry.id,
  );

/** The wire vocabulary: atoms an agent may ask warhol to render. */
export const CHAT_ATOM_AGENT_RENDERABLE_COMPONENT_IDS: readonly ChatAtomAgentComponentId[] =
  CHAT_ATOM_AGENT_COMPONENT_MAP.filter((entry) => !entry.clientOnly).map(
    (entry) => entry.id,
  );

// why: the catalog is what an agent reads before it picks an atom, and until
// now "only renders nested" lived in `whenToUse` PROSE on one of the two
// entries that carry the rule -- `question-form-card` never said it, so an
// agent that took its keyProps at face value emitted a standalone card that
// warhol refused and Carrie dropped, with nothing drawn and no error. The
// flag is a field so `ASK_PUNTT_NESTED_ONLY_CHAT_ATOM_IDS` can be pinned to
// it instead of to a sentence.

/** Atoms that only render inside another atom's slot, per the map. */
export const CHAT_ATOM_NESTED_ONLY_COMPONENT_IDS: readonly ChatAtomAgentComponentId[] =
  CHAT_ATOM_AGENT_COMPONENT_MAP.filter((entry) => entry.nestedOnly).map(
    (entry) => entry.id,
  );
